import Phaser from 'phaser';
import type { BuildingTypeId } from '../sim/buildings/definitions';
import { FX_PUFF_KEY } from './combatFx';

/**
 * Living buildings (D138): chimney smoke, forge and lamp glows, sparks, the
 * beacon's blinking light, the nest's slow pulse — anchored on the painted art
 * (u, v = fraction of the art's width / height). Purely visual; finished
 * buildings only, and none on the Low graphics setting.
 */
type Fx =
  | { readonly kind: 'smoke'; readonly u: number; readonly v: number; readonly tint?: number }
  | { readonly kind: 'glow'; readonly u: number; readonly v: number; readonly color: number; readonly r: number; readonly mode: 'pulse' | 'flicker' | 'blink' }
  | { readonly kind: 'fire'; readonly u: number; readonly v: number; readonly size: number }
  | { readonly kind: 'sparks'; readonly u: number; readonly v: number; readonly color: number };

const smoke = (u: number, v: number, tint?: number): Fx => ({ kind: 'smoke', u, v, tint });
const glow = (u: number, v: number, color: number, r: number, mode: 'pulse' | 'flicker' | 'blink' = 'pulse'): Fx => ({ kind: 'glow', u, v, color, r, mode });
const fire = (u: number, v: number, size = 1): Fx => ({ kind: 'fire', u, v, size });
const sparks = (u: number, v: number, color = 0xffd27a): Fx => ({ kind: 'sparks', u, v, color });

export const AMBIENT: Partial<Record<BuildingTypeId, readonly Fx[]>> = {
  farm: [smoke(0.55, 0.14)],
  sawmill: [smoke(0.81, 0.07)],
  survivor_housing: [smoke(0.63, 0.13)],
  scrap_yard: [smoke(0.67, 0.05), sparks(0.38, 0.55)],
  workshop: [glow(0.65, 0.58, 0xff8a3a, 22, 'flicker'), smoke(0.69, 0.13), sparks(0.65, 0.6)],
  warehouse: [glow(0.37, 0.5, 0xffb35c, 13, 'flicker')],
  gate: [glow(0.47, 0.33, 0xffb35c, 10, 'flicker')],
  watchtower: [glow(0.67, 0.36, 0xffc070, 10, 'flicker')],
  gun_tower: [smoke(0.6, 0.07), smoke(0.79, 0.17)],
  flame_tower: [fire(0.32, 0.05, 0.6)],
  generator: [glow(0.4, 0.35, 0xffc35a, 46, 'pulse'), sparks(0.4, 0.35, 0xbfe6ff)],
  research_lab: [glow(0.73, 0.54, 0xfff08a, 30, 'pulse')],
  horde_beacon: [glow(0.25, 0.05, 0xff3a24, 16, 'blink')],
  infected_nest: [glow(0.5, 0.42, 0xff3a3a, 62, 'pulse'), smoke(0.65, 0.06, 0x7d8a5a)],
  survivor_camp: [fire(0.5, 0.53, 1), glow(0.5, 0.53, 0xff9a40, 26, 'flicker'), smoke(0.5, 0.45)],
};

interface Glow {
  readonly img: Phaser.GameObjects.Image;
  readonly mode: 'pulse' | 'flicker' | 'blink';
  readonly seed: number;
}

/** The ambient effects of one building; `update` animates the glows. */
export class Ambient {
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private readonly glows: Glow[] = [];

  constructor(scene: Phaser.Scene, type: BuildingTypeId, art: { x: number; y: number; width: number; height: number }, depth: number, seed: number) {
    for (const fx of AMBIENT[type] ?? []) {
      const x = art.x + fx.u * art.width;
      const y = art.y + fx.v * art.height;
      switch (fx.kind) {
        case 'smoke':
          this.objects.push(
            scene.add
              .particles(x, y, FX_PUFF_KEY, {
                x: { min: -2, max: 2 },
                speedY: { min: -16, max: -9 },
                speedX: { min: 2, max: 7 },
                lifespan: 2200,
                scale: { start: 0.22, end: 1 },
                alpha: { start: 0.55, end: 0 },
                tint: fx.tint ?? 0x8a8580,
                frequency: 420,
              })
              .setDepth(depth + 0.0003),
          );
          break;
        case 'fire':
          this.objects.push(
            scene.add
              .particles(x, y, FX_PUFF_KEY, {
                x: { min: -4 * fx.size, max: 4 * fx.size },
                speedY: { min: -20 * fx.size, max: -8 * fx.size },
                lifespan: 480,
                scale: { start: 0.32 * fx.size, end: 0.05 },
                alpha: { start: 0.9, end: 0 },
                tint: [0xffd16a, 0xff8a3a, 0xe8502a],
                blendMode: 'ADD',
                frequency: 70,
              })
              .setDepth(depth + 0.0002),
          );
          break;
        case 'sparks': {
          const e = scene.add
            .particles(x, y, FX_PUFF_KEY, {
              speed: { min: 14, max: 40 },
              angle: { min: 200, max: 340 },
              gravityY: 60,
              lifespan: 420,
              scale: { start: 0.09, end: 0.02 },
              alpha: { start: 1, end: 0 },
              tint: fx.color,
              blendMode: 'ADD',
              emitting: false,
            })
            .setDepth(depth + 0.0002);
          // Short bursts now and then, not a stream.
          const timer = scene.time.addEvent({ delay: 900 + (seed % 7) * 130, loop: true, callback: () => e.explode(5 + (seed % 4)) });
          this.objects.push(e, { destroy: () => timer.remove() } as unknown as Phaser.GameObjects.GameObject);
          break;
        }
        case 'glow': {
          const img = scene.add
            .image(x, y, FX_PUFF_KEY)
            .setBlendMode(Phaser.BlendModes.ADD)
            .setTint(fx.color)
            .setDisplaySize(fx.r * 2, fx.r * 1.6)
            .setDepth(depth + 0.0001);
          this.objects.push(img);
          this.glows.push({ img, mode: fx.mode, seed: seed * 0.37 + this.glows.length });
          break;
        }
      }
    }
  }

  update(now: number): void {
    const t = now / 1000;
    for (const g of this.glows) {
      let a: number;
      if (g.mode === 'pulse') a = 0.5 + 0.3 * Math.sin(t * 1.6 + g.seed);
      else if (g.mode === 'blink') a = Math.sin(t * 3.2 + g.seed) > 0.2 ? 0.95 : 0.12;
      else a = 0.55 + 0.18 * Math.sin(t * 9.1 + g.seed) + 0.12 * Math.sin(t * 23.7 + g.seed * 2);
      g.img.setAlpha(a);
    }
  }

  destroy(): void {
    for (const o of this.objects) o.destroy();
    this.objects.length = 0;
    this.glows.length = 0;
  }
}
